import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';

// the customer's warehouse (klaris.calendar_v.week_beginning) uses Monday-start weeks.
// isoWeek makes startOf('isoWeek') resolve to Monday to match that convention.
dayjs.extend(isoWeek);

export interface CustomRange {
  currentStart: string;
  currentEnd: string;
  compareStart?: string;
  compareEnd?: string;
  /** When false, KPI deltas and header comparison are hidden. Defaults to true. */
  compareEnabled?: boolean;
}

interface PeriodComparePickerProps {
  /** Currently applied custom range (null = using the default server period). */
  value: CustomRange | null;
  /** Fallback range to seed the editor when nothing custom is applied yet. */
  activeStart?: string | null;
  activeEnd?: string | null;
  onApply: (range: CustomRange) => void;
  disabled?: boolean;
  /** When true, comparison is always required and the compare toggle is hidden. */
  compareRequired?: boolean;
}

type PresetId =
  | 'this_week'
  | 'last_week'
  | 'last_7'
  | 'last_30'
  | 'last_90'
  | 'this_month'
  | 'last_month'
  | 'custom';

interface PresetDef {
  id: PresetId;
  label: string;
  range: () => { start: Dayjs; end: Dayjs };
}

const ISO = 'YYYY-MM-DD';

/**
 * Latest calendar day expected to have warehouse data (T-1).
 * Scoring / projection already treat data as complete through yesterday.
 */
export function dataAsOfDay(today: Dayjs = dayjs()): Dayjs {
  return today.subtract(1, 'day').startOf('day');
}

/**
 * Data-complete week window for defaults and the "This week" preset.
 * Mon → yesterday when this week has ≥1 complete day; on Monday (no data
 * days yet in the current week), last full Mon–Sun week.
 */
export function dataCompleteWeekRange(today: Dayjs = dayjs()): { start: Dayjs; end: Dayjs } {
  const asOf = dataAsOfDay(today);
  const weekStart = today.startOf('isoWeek');
  if (asOf.isBefore(weekStart, 'day')) {
    const lastWeekStart = weekStart.subtract(1, 'week');
    return { start: lastWeekStart, end: lastWeekStart.add(6, 'day') };
  }
  return { start: weekStart, end: asOf };
}

const PRESETS: PresetDef[] = [
  {
    id: 'this_week',
    label: 'This week',
    range: () => dataCompleteWeekRange(),
  },
  {
    id: 'last_week',
    label: 'Last week',
    range: () => ({
      start: dayjs().startOf('isoWeek').subtract(1, 'week'),
      end: dayjs().startOf('isoWeek').subtract(1, 'day'),
    }),
  },
  {
    id: 'last_7',
    label: 'Last 7 days',
    range: () => ({ start: dayjs().subtract(6, 'day'), end: dayjs() }),
  },
  {
    id: 'last_30',
    label: 'Last 30 days',
    range: () => ({ start: dayjs().subtract(29, 'day'), end: dayjs() }),
  },
  {
    id: 'last_90',
    label: 'Last 90 days',
    range: () => ({ start: dayjs().subtract(89, 'day'), end: dayjs() }),
  },
  {
    id: 'this_month',
    label: 'This month',
    range: () => ({ start: dayjs().startOf('month'), end: dayjs() }),
  },
  {
    id: 'last_month',
    label: 'Last month',
    range: () => ({
      start: dayjs().startOf('month').subtract(1, 'month'),
      end: dayjs().startOf('month').subtract(1, 'day'),
    }),
  },
];

/**
 * Default period on first load: data-complete ISO week-to-date (Mon → yesterday).
 * On Monday, last complete Mon–Sun week. Weeks match warehouse calendar_v.
 */
export function defaultDialOpRange(): CustomRange {
  const { start, end } = dataCompleteWeekRange();
  return {
    currentStart: start.format(ISO),
    currentEnd: end.format(ISO),
    compareEnabled: true,
  };
}

function autoCompare(start: Dayjs, end: Dayjs): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  const weekStart = today.startOf('isoWeek');
  // In-progress / data-complete WTD (Mon → ≤ today): same weekday span one week earlier.
  if (!start.isBefore(weekStart, 'day') && !end.isAfter(today, 'day')) {
    return { start: start.subtract(1, 'week'), end: end.subtract(1, 'week') };
  }
  const span = end.diff(start, 'day');
  const cmpEnd = start.subtract(1, 'day');
  const cmpStart = cmpEnd.subtract(span, 'day');
  return { start: cmpStart, end: cmpEnd };
}

export function isDialOpCompareEnabled(range: CustomRange | null | undefined): boolean {
  return range?.compareEnabled !== false;
}

export function resolveDialOpComparePeriod(
  range: CustomRange,
): { start: string; end: string } | null {
  if (!isDialOpCompareEnabled(range)) return null;
  if (range.compareStart && range.compareEnd) {
    return { start: range.compareStart, end: range.compareEnd };
  }
  const auto = autoCompare(dayjs(range.currentStart), dayjs(range.currentEnd));
  return { start: auto.start.format(ISO), end: auto.end.format(ISO) };
}

export function snapshotMatchesRange(
  snapshot: { current_period?: { start?: string | null; end?: string | null } | null; prior_period?: { start?: string | null; end?: string | null } | null },
  range: CustomRange | null,
): boolean {
  if (!range?.currentStart || !range?.currentEnd) return false;
  const cur = snapshot.current_period;
  if (cur?.start !== range.currentStart || cur?.end !== range.currentEnd) return false;

  if (!isDialOpCompareEnabled(range)) {
    return (
      snapshot.prior_period?.start === range.currentStart
      && snapshot.prior_period?.end === range.currentEnd
    );
  }

  const resolved = resolveDialOpComparePeriod(range);
  if (!resolved) return true;
  return (
    snapshot.prior_period?.start === resolved.start
    && snapshot.prior_period?.end === resolved.end
  );
}

function formatRangeLabel(start?: string | null, end?: string | null): string {
  if (!start || !end) return '—';
  if (start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

function fmt(d?: string | Dayjs | null): string {
  if (!d) return '—';
  const dj = dayjs.isDayjs(d) ? d : dayjs(d);
  if (!dj.isValid()) return '—';
  return dj.format('MMM D, YYYY');
}

function RangeDay(
  props: PickersDayProps & { selectedStart?: Dayjs | null; selectedEnd?: Dayjs | null },
) {
  const theme = useTheme();
  const { selectedStart, selectedEnd, day, ...other } = props;
  const isStart = selectedStart && day.isSame(selectedStart, 'day');
  const isEnd = selectedEnd && day.isSame(selectedEnd, 'day');
  const inRange =
    selectedStart &&
    selectedEnd &&
    day.isAfter(selectedStart, 'day') &&
    day.isBefore(selectedEnd, 'day');

  return (
    <PickersDay
      {...other}
      day={day}
      selected={Boolean(isStart || isEnd)}
      sx={{
        ...(inRange
          ? {
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              borderRadius: 0,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.22) },
            }
          : {}),
      }}
    />
  );
}

export default function PeriodComparePicker({
  value,
  activeStart,
  activeEnd,
  onApply,
  disabled,
  compareRequired = false,
}: PeriodComparePickerProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [start, setStart] = React.useState<Dayjs | null>(null);
  const [end, setEnd] = React.useState<Dayjs | null>(null);
  const [presetId, setPresetId] = React.useState<PresetId>('custom');
  const [compareOn, setCompareOn] = React.useState(true);
  const [compareMode, setCompareMode] = React.useState<'auto' | 'custom'>('auto');
  const [cmpStart, setCmpStart] = React.useState('');
  const [cmpEnd, setCmpEnd] = React.useState('');

  const displayStart = value?.currentStart ?? activeStart ?? null;
  const displayEnd = value?.currentEnd ?? activeEnd ?? null;
  const triggerLabel =
    displayStart && displayEnd ? formatRangeLabel(displayStart, displayEnd) : 'Select period';

  const open = (e: React.MouseEvent<HTMLElement>) => {
    const fallback = dataCompleteWeekRange();
    const seedStart = value?.currentStart ?? activeStart ?? fallback.start.format(ISO);
    const seedEnd = value?.currentEnd ?? activeEnd ?? fallback.end.format(ISO);
    setStart(dayjs(seedStart));
    setEnd(dayjs(seedEnd));
    setPresetId('custom');
    if (value?.compareEnabled === false && !compareRequired) {
      setCompareOn(false);
      setCompareMode('auto');
      setCmpStart('');
      setCmpEnd('');
    } else if (value?.compareStart && value?.compareEnd) {
      setCompareOn(true);
      const auto = autoCompare(dayjs(seedStart), dayjs(seedEnd));
      const isAuto =
        value.compareStart === auto.start.format(ISO)
        && value.compareEnd === auto.end.format(ISO);
      setCompareMode(isAuto ? 'auto' : 'custom');
      setCmpStart(value.compareStart);
      setCmpEnd(value.compareEnd);
    } else {
      setCompareOn(true);
      setCompareMode('auto');
      setCmpStart('');
      setCmpEnd('');
    }
    setAnchorEl(e.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const pickDay = (day: Dayjs | null) => {
    if (!day) return;
    setPresetId('custom');
    if (!start || (start && end)) {
      setStart(day);
      setEnd(null);
    } else if (day.isBefore(start, 'day')) {
      setStart(day);
    } else {
      setEnd(day);
    }
  };

  const applyPreset = (p: PresetDef) => {
    const r = p.range();
    setStart(r.start);
    setEnd(r.end);
    setPresetId(p.id);
  };

  const rangeReady = Boolean(start && end && !end.isBefore(start, 'day'));
  const autoPreview = rangeReady && start && end ? autoCompare(start, end) : null;

  const effectiveCompareOn = compareRequired || compareOn;

  const compareValid =
    !effectiveCompareOn ||
    compareMode === 'auto' ||
    (!!cmpStart && !!cmpEnd && !dayjs(cmpEnd).isBefore(dayjs(cmpStart), 'day'));

  const canApply = rangeReady && compareValid;

  const apply = () => {
    if (!start || !end || !canApply) return;
    const range: CustomRange = {
      currentStart: start.format(ISO),
      currentEnd: end.format(ISO),
      compareEnabled: effectiveCompareOn,
    };
    if (effectiveCompareOn) {
      if (compareMode === 'custom') {
        range.compareStart = cmpStart;
        range.compareEnd = cmpEnd;
      }
      // auto → leave compare fields unset; backend derives preceding period
    } else {
      // no comparison: point comparison at the same window so deltas read ~0
      range.compareStart = start.format(ISO);
      range.compareEnd = end.format(ISO);
    }
    onApply(range);
    close();
  };

  const leftMonth = (end ?? dayjs()).subtract(1, 'month');
  const rightMonth = end ?? dayjs();

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={open}
        disabled={disabled}
        startIcon={<CalendarMonthRounded sx={{ fontSize: 18 }} />}
        endIcon={<KeyboardArrowDownRounded sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          px: 1.5,
          color: 'text.primary',
          borderColor: 'divider',
          whiteSpace: 'nowrap',
        }}
      >
        {triggerLabel}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, overflow: 'hidden', mt: 0.5 } } }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ maxWidth: '96vw' }}>
          {/* Preset rail */}
          <Stack
            spacing={0.25}
            sx={{
              minWidth: 168,
              p: 1.5,
              borderRight: { md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: 'divider',
              bgcolor: (t) => t.tokens?.surface?.sunken ?? 'action.hover',
            }}
          >
            {PRESETS.map((p) => (
              <Button
                key={p.id}
                size="small"
                onClick={() => applyPreset(p)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontWeight: presetId === p.id ? 700 : 500,
                  color: presetId === p.id ? 'primary.main' : 'text.primary',
                  bgcolor: presetId === p.id ? 'action.selected' : 'transparent',
                  borderRadius: 1.5,
                  px: 1.25,
                }}
              >
                {p.label}
              </Button>
            ))}
          </Stack>

          {/* Calendar + compare */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {rangeReady ? `${fmt(start)} – ${fmt(end)}` : start ? `${fmt(start)} – …` : 'Pick a range'}
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {[leftMonth, rightMonth].map((refMonth, i) => (
                  <DateCalendar
                    key={i}
                    value={null}
                    referenceDate={refMonth}
                    views={['day']}
                    disableFuture
                    onChange={(d) => pickDay(d as Dayjs | null)}
                    slots={{ day: RangeDay as any }}
                    slotProps={{
                      day: { selectedStart: start, selectedEnd: end } as any,
                    }}
                    sx={{
                      width: 300,
                      m: 0,
                      '& .MuiPickersCalendarHeader-root': { mt: 0, mb: 0.5, pl: 1.5, pr: 1 },
                    }}
                  />
                ))}
              </Stack>
            </LocalizationProvider>

            <Divider sx={{ my: 1 }} />

            {!compareRequired ? (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={compareOn}
                      onChange={(e) => setCompareOn(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" fontWeight={600}>Compare to another period</Typography>}
                />
              </Stack>
            ) : (
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Compare to another period
              </Typography>
            )}

            {effectiveCompareOn ? (
              <RadioGroup
                row
                value={compareMode}
                onChange={(e) => setCompareMode(e.target.value as 'auto' | 'custom')}
                sx={{ pl: 0.5 }}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      The period just before
                      {autoPreview ? (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {' '}({fmt(autoPreview.start)} – {fmt(autoPreview.end)})
                        </Box>
                      ) : null}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Custom</Typography>}
                />
              </RadioGroup>
            ) : null}

            {effectiveCompareOn && compareMode === 'custom' ? (
              <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, mb: 0.5, pl: 0.5 }}>
                <TextField
                  label="Compare from"
                  type="date"
                  size="small"
                  value={cmpStart}
                  onChange={(e) => setCmpStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Compare to"
                  type="date"
                  size="small"
                  value={cmpEnd}
                  onChange={(e) => setCmpEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            ) : null}

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button size="small" onClick={close} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={apply}
                disabled={!canApply}
                sx={{ textTransform: 'none' }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  );
}
